import { type BitrateChartData } from "../utils/bitrateHistory";
import StreamBitrateTooltip from "./StreamBitrateTooltip";
import { useId, useMemo, useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { streamCardSurfaceColors } from "../theme";

type StreamBitrateChartTinyProps = {
  bitrates: BitrateChartData;
  height?: number;
  showGrid?: boolean;
  showValue?: boolean;
};

const StreamBitrateChartTiny = ({ bitrates, height = 48, showGrid = false, showValue = false }: StreamBitrateChartTinyProps) => {
  const theme = useTheme();
  const gradientId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const chartData = bitrates.points;
  const latestBitrate = chartData.at(-1)?.bitrate;
  const [start, end] = bitrates.domain;
  const timeTicks = [start, start + (end - start) / 3, start + (end - start) * 2 / 3, end];
  const lineColor = theme.palette.primary.main;

  const maxBitrate = useMemo(() => {
    if (!chartData.length) {
      return 1;
    }
    const maxValue = chartData.reduce((max, point) => ((point.bitrate ?? 0) > max ? point.bitrate! : max), 0);
    return maxValue > 0 ? maxValue : 1;
  }, [chartData]);

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      {showValue ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", columnGap: 1 }}>
          <Typography variant="caption" sx={{ color: streamCardSurfaceColors[theme.palette.mode].muted }}>
            Bitrate
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {typeof latestBitrate === "number" ? latestBitrate.toFixed(2) : "—"}
            <Box component="span" sx={{ ml: 0.5, fontSize: "0.75rem", fontWeight: 400, color: streamCardSurfaceColors[theme.palette.mode].muted }}>Mbit/s</Box>
          </Typography>
        </Box>
      ) : null}
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height,
          px: showGrid ? 0 : 0.5,
          backgroundColor: "inherit",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: showGrid ? 0 : 2 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={showGrid ? 0.18 : 0.45} />
                <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={showGrid ? 0.02 : 0.05} />
              </linearGradient>
            </defs>
            <XAxis hide type="number" dataKey="timestamp" domain={bitrates.domain} ticks={showGrid ? timeTicks : undefined} allowDataOverflow />
            <YAxis hide domain={[0, maxBitrate]} ticks={showGrid ? [0, maxBitrate / 3, maxBitrate * 2 / 3, maxBitrate] : undefined} />
            {showGrid ? <CartesianGrid stroke={streamCardSurfaceColors[theme.palette.mode].border} strokeOpacity={0.65} syncWithTicks /> : null}
            <RechartsTooltip
              content={({ active, payload }) => <StreamBitrateTooltip active={active} payload={payload} anchorEl={containerRef.current} compact />}
              cursor={{ stroke: theme.palette.primary.main, strokeWidth: 1, strokeDasharray: "3 3" }}
            />
            <Area
              type="linear"
              connectNulls={false}
              dataKey="bitrate"
              stroke={lineColor}
              fill={`url(#${gradientId})`}
              fillOpacity={1}
              strokeWidth={showGrid ? 1.75 : 1}
              isAnimationActive={false}
              dot={showGrid ? false : { r: 1 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default StreamBitrateChartTiny;
